import { useEffect, useState, useMemo, useCallback } from 'react';
import type { CatchballTierTurn } from '../types/prioritization/catchballTierTurn';
import type { CatchballLoopState } from '../types/prioritization/catchballLoop';
import { useCatchballTierTurnStore } from '../stores/prioritization/catchballTierTurnStore';
import { useEnterpriseIdentityStore } from '../stores/prioritization/enterpriseIdentityStore';
import { useCatchballLoopStore } from '../stores/prioritization/catchballLoopStore';
import { useDegapProcessStore } from '../stores/prioritization/degapProcessStore';
import { computeOrgAwareDetails } from './prioritization/useOrgAwareCatchball';
import { getSafeSupabaseClient } from '../lib/supabaseSync';

export interface LegacyLoopRow {
  loopId: string;
  cycleId: string;
  state: CatchballLoopState;
  loopNumber: number | null;
  /** Present when the row comes from Supabase (used for merge sort). */
  updatedAt?: string | null;
  /**
   * 'executive' rows come from the creator/exec-review query below.
   * 'branch' rows are surfaced because the org-chart Ball (recipientOrgUnitIds)
   * is currently sitting with this user's org unit — distinct data source, same
   * "ball in court" shape so it can ride the same My Tasks / nav badge plumbing.
   */
  holderRole?: 'executive' | 'branch';
  /** For 'branch' rows: which org unit(s) currently hold the ball, for the subtitle. */
  currentHolderLabel?: string | null;
}

const EXECUTIVE_STATES = [
  'drafting',
  'executive_review',
  'output_ready',
  'returned_to_top',
  'proposed',
  'PROPOSED',
  'FINAL_REJECTED',
] as const;

/**
 * Active Catchball items where the current user should take action (level turns + legacy exec rows).
 */
export function useCatchballBallInCourtData(currentUserId: string | null) {
  const fetchActiveForUser = useCatchballTierTurnStore(s => s.fetchActiveForUser);
  const fetchIdentity = useEnterpriseIdentityStore(s => s.fetchFromSupabase);
  const fetchLoops = useCatchballLoopStore(s => s.fetchFromSupabase);
  const allLoops = useCatchballLoopStore(s => s.loops);
  const orgUnits = useEnterpriseIdentityStore(s => s.orgUnits);
  const userNodeIndex = useEnterpriseIdentityStore(s => s.userNodeIndex);
  const cycles = useDegapProcessStore(s => s.cycles);

  const [activeTurns, setActiveTurns] = useState<
    Array<{ loopId: string; cycleId: string; turn: CatchballTierTurn }>
  >([]);
  const [legacyLoops, setLegacyLoops] = useState<LegacyLoopRow[]>([]);

  useEffect(() => {
    void fetchIdentity();
  }, [fetchIdentity]);

  const refetchBallInCourt = useCallback(async () => {
    if (!currentUserId) {
      setActiveTurns([]);
      setLegacyLoops([]);
      return;
    }

    try {
      const results = await fetchActiveForUser(currentUserId);
      setActiveTurns(results);
      const turnCycleIds = [...new Set(results.map((r) => r.cycleId).filter(Boolean))] as string[];
      turnCycleIds.forEach((cid) => {
        void fetchLoops(cid);
      });
    } catch {
      setActiveTurns([]);
    }

    // Workspace-wide fetch so org-chart Ball recipients (below) can be matched
    // against loops they didn't create and aren't an explicit level-turn holder on.
    void fetchLoops();

    const supabase = getSafeSupabaseClient();
    if (supabase) {
      try {
        const { data } = await supabase
          .from('catchball_loops')
          .select('id, cycle_id, state, loop_number, updated_at')
          .eq('created_by', currentUserId)
          .in('state', [...EXECUTIVE_STATES]);
        if (!data) {
          setLegacyLoops([]);
        } else {
          const rows: LegacyLoopRow[] = data.map((row: Record<string, unknown>) => ({
            loopId: row.id as string,
            cycleId: row.cycle_id as string,
            state: row.state as CatchballLoopState,
            loopNumber: row.loop_number as number | null,
            updatedAt: (row.updated_at as string | null) ?? null,
            holderRole: 'executive',
          }));
          setLegacyLoops(rows);
          const legacyCycleIds = [...new Set(rows.map((r) => r.cycleId))];
          legacyCycleIds.forEach((cid) => void fetchLoops(cid));
        }
      } catch {
        setLegacyLoops([]);
      }
    } else {
      setLegacyLoops([]);
    }
  }, [currentUserId, fetchActiveForUser, fetchLoops]);

  useEffect(() => {
    void refetchBallInCourt();
  }, [refetchBallInCourt]);

  // Org-chart Balls where the ball is currently sitting with this user's org
  // unit (or a unit above them, per computeLoopAccess's subtree rule) — the
  // "branch recipient" path used by RequestComposerPage/CatchballDashboard,
  // which has no per-user turn or creator row and was otherwise invisible to
  // My Tasks / nav badges for anyone but the loop's creator.
  const orgBallLoops = useMemo<LegacyLoopRow[]>(() => {
    if (!currentUserId || allLoops.length === 0) return [];
    const cycleOrgScopeById = new Map(cycles.map((c) => [c.id, c.orgScopeNodeId ?? null]));
    const rows: LegacyLoopRow[] = [];
    for (const loop of allLoops) {
      if (loop.state === 'closed') continue;
      const details = computeOrgAwareDetails(
        loop,
        currentUserId,
        orgUnits,
        userNodeIndex,
        cycleOrgScopeById.get(loop.cycleId) ?? null,
      );
      if (!details.canActOnLoop || !details.ballIsWithMe) continue;
      rows.push({
        loopId: loop.id,
        cycleId: loop.cycleId,
        state: loop.state,
        loopNumber: loop.loopNumber,
        updatedAt: loop.updatedAt?.toISOString?.() ?? null,
        holderRole: 'branch',
        currentHolderLabel: details.currentHolderLabel,
      });
    }
    return rows;
  }, [currentUserId, allLoops, orgUnits, userNodeIndex, cycles]);

  const filteredLegacyLoops = useMemo(() => {
    const tierTurnLoopIds = new Set(activeTurns.map(t => t.loopId));
    const merged = [...legacyLoops];
    const seenLoopIds = new Set(merged.map((r) => r.loopId));
    for (const row of orgBallLoops) {
      if (seenLoopIds.has(row.loopId)) continue;
      seenLoopIds.add(row.loopId);
      merged.push(row);
    }
    return merged.filter(r => !tierTurnLoopIds.has(r.loopId));
  }, [activeTurns, legacyLoops, orgBallLoops]);

  return { activeTurns, filteredLegacyLoops, refetchBallInCourt };
}
